import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Upload, Loader2, X, Clock, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  unverified: { label: "Non vérifiée", icon: AlertCircle, color: "bg-zinc-100 text-zinc-600", border: "border-zinc-200" },
  pending: { label: "En cours d'examen", icon: Clock, color: "bg-amber-50 text-amber-700", border: "border-amber-200" },
  verified: { label: "Vérifiée", icon: CheckCircle2, color: "bg-emerald-50 text-emerald-700", border: "border-emerald-200" },
  rejected: { label: "Refusee", icon: X, color: "bg-red-50 text-red-700", border: "border-red-200" },
};

const toFullUrl = (u) => (u?.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

function PhotoUploadSlot({ label, photoUrl, setPhotoUrl, uploading, setUploading, testId }) {
  const fileRef = useRef(null);
  const previewSrc = toFullUrl(photoUrl);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrl(data.url);
      toast.success("Photo televersee");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">{label} <span className="text-red-500">*</span></label>
      {photoUrl ? (
        <div className="relative rounded-xl overflow-hidden border border-zinc-200 bg-zinc-100">
          <img src={previewSrc} alt={label} className="w-full max-h-56 object-cover" data-testid={`${testId}-preview`} />
          <button
            type="button"
            onClick={() => setPhotoUrl("")}
            className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"
            data-testid={`${testId}-remove`}
          >
            <X className="h-4 w-4 text-zinc-700" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full h-32 rounded-xl border-2 border-dashed border-zinc-300 hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors flex flex-col items-center justify-center gap-2 text-zinc-500"
          data-testid={`${testId}-btn`}
        >
          {uploading ? (
            <><Loader2 className="h-5 w-5 animate-spin" /><span className="text-xs">Upload en cours...</span></>
          ) : (
            <><Upload className="h-5 w-5" /><span className="text-xs font-medium">Ajouter une photo</span><span className="text-[10px]">JPG / PNG, 5 Mo max</span></>
          )}
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid={`${testId}-input`} />
    </div>
  );
}

/**
 * Identity verification card - hosts upload TWO photos of an ID document
 * (e.g. recto + verso of a driver's license) for admin review.
 */
export default function IdentityVerificationCard({ verification, onUpdated }) {
  const [photo1, setPhoto1] = useState("");
  const [photo2, setPhoto2] = useState("");
  const [selfie, setSelfie] = useState("");
  const [up1, setUp1] = useState(false);
  const [up2, setUp2] = useState(false);
  const [upSelfie, setUpSelfie] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const status = verification?.status || "unverified";
  const meta = STATUS_META[status] || STATUS_META.unverified;
  const Icon = meta.icon;

  const submit = async () => {
    if (!photo1 || !photo2 || !selfie) { toast.error("Les trois photos sont obligatoires"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/profile/verification", {
        photo_url: photo1,
        photo_url_2: photo2,
        selfie_url: selfie,
      });
      toast.success("Vérification envoyée a l'administrateur");
      setPhoto1(""); setPhoto2(""); setSelfie("");
      onUpdated?.(data.identity_verification);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const canSubmit = ["unverified", "rejected"].includes(status);
  const uploading = up1 || up2 || upSelfie;

  return (
    <Card className="border border-zinc-200 rounded-2xl" data-testid="identity-verification-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" /> Vérification d'identité
            </CardTitle>
            <CardDescription className="mt-1">
              Requise avant de publier un terrain. Téléversez deux photos d'un document officiel (recto et verso) ainsi qu'un selfie tenant ce document a cote de votre visage.
            </CardDescription>
          </div>
          <Badge className={`${meta.color} border ${meta.border} gap-1 flex-shrink-0`} data-testid="verification-status-badge">
            <Icon className="h-3 w-3" /> {meta.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Existing submission preview (1, 2 or 3 photos) */}
        {(verification?.photo_url || verification?.photo_url_2 || verification?.selfie_url) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {verification?.photo_url && (
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 overflow-hidden">
                <img src={toFullUrl(verification.photo_url)} alt="Document recto" className="w-full max-h-56 object-cover" data-testid="verification-current-photo-1" />
                <p className="text-[10px] text-center text-zinc-500 py-1">Document - recto</p>
              </div>
            )}
            {verification?.photo_url_2 && (
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 overflow-hidden">
                <img src={toFullUrl(verification.photo_url_2)} alt="Document verso" className="w-full max-h-56 object-cover" data-testid="verification-current-photo-2" />
                <p className="text-[10px] text-center text-zinc-500 py-1">Document - verso</p>
              </div>
            )}
            {verification?.selfie_url && (
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 overflow-hidden">
                <img src={toFullUrl(verification.selfie_url)} alt="Selfie" className="w-full max-h-56 object-cover" data-testid="verification-current-selfie" />
                <p className="text-[10px] text-center text-zinc-500 py-1">Selfie avec document</p>
              </div>
            )}
          </div>
        )}

        {status === "pending" && (
          <p className="text-sm text-amber-700 p-3 rounded-xl bg-amber-50 border border-amber-100">
            Votre demande est en cours d'examen. Vous recevrez une notification des qu'elle sera traitee.
          </p>
        )}

        {status === "rejected" && verification?.reject_reason && (
          <p className="text-sm text-red-700 p-3 rounded-xl bg-red-50 border border-red-100">
            <span className="font-semibold">Motif de refus :</span> {verification.reject_reason}
          </p>
        )}

        {status === "verified" && (
          <p className="text-sm text-emerald-700 p-3 rounded-xl bg-emerald-50 border border-emerald-100">
            Votre identité est vérifiée. Vous pouvez publier vos terrains librement.
          </p>
        )}

        {canSubmit && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <PhotoUploadSlot
                label="Document - recto"
                photoUrl={photo1}
                setPhotoUrl={setPhoto1}
                uploading={up1}
                setUploading={setUp1}
                testId="verification-photo-1"
              />
              <PhotoUploadSlot
                label="Document - verso"
                photoUrl={photo2}
                setPhotoUrl={setPhoto2}
                uploading={up2}
                setUploading={setUp2}
                testId="verification-photo-2"
              />
              <PhotoUploadSlot
                label="Selfie avec document"
                photoUrl={selfie}
                setPhotoUrl={setSelfie}
                uploading={upSelfie}
                setUploading={setUpSelfie}
                testId="verification-selfie"
              />
            </div>

            <p className="text-xs text-zinc-500 -mt-1">
              Le selfie sert a confirmer que vous etes bien la personne sur le document. Tenez le document a cote de votre visage avec les informations lisibles.
            </p>

            <Button
              onClick={submit}
              disabled={submitting || uploading || !photo1 || !photo2 || !selfie}
              className="w-full bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl"
              data-testid="verification-submit-btn"
            >
              {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Envoi...</>) : "Envoyer pour verification"}
            </Button>

            <p className="text-xs text-zinc-400">
              Vos donnees sont uniquement utilisees pour valider votre identité. Aucune utilisation commerciale.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
